import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../../context/AuthContext';
import { apiUrl } from '../../../utils/apiBase';
import {
  PENDING_DOWNLOAD_KEY,
  resolveDownloadUrl,
  triggerBrowserDownload,
} from '../../../utils/downloadResume';
import PasswordVisibilityIcon from './PasswordVisibilityIcon';
import { getConfig } from '../../../config/appConfig';
import { defaultLogoCdnUrl, legacyApiPath } from '../../../config/environments';
import { CloudAuthButtons } from '../../auth/cloudAuth';

export default function DownloadModal({ isOpen, onClose }) {
  const { login, isAuthenticated } = useAuth();

  // modes: 'signin' | 'register' | 'verify' | 'forgot' | 'reset-password'
  const [mode, setMode] = useState('signin');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [showPass, setShowPass] = useState(false);

  // Password-reset specific state
  const [resetOtp, setResetOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const [forgotAwaitingCode, setForgotAwaitingCode] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (isAuthenticated) {
        triggerDownload();
        onClose();
      } else {
        setMode('signin');
        setCaptchaVerified(false);
        setResetOtp('');
        setNewPassword('');
        setConfirmPassword('');
      }
    }
  }, [isOpen, isAuthenticated]);

  useEffect(() => {
    setCaptchaVerified(false);
  }, [mode]);

  useEffect(() => {
    if (isOpen && (mode === 'register' || mode === 'signin') && window.grecaptcha) {
      const containerId = mode === 'signin' ? 'download-recaptcha-signin' : 'download-recaptcha-register';
      const timer = setTimeout(() => {
        try {
          window.grecaptcha.render(containerId, {
            sitekey: getConfig('recaptcha.siteKey'),
            callback: () => setCaptchaVerified(true),
            'expired-callback': () => setCaptchaVerified(false),
          });
        } catch (e) {
          // Already rendered
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen, mode]);

  if (!isOpen) return null;

  function switchMode(next) {
    setMode(next);
    if (next !== 'forgot') {
      setForgotAwaitingCode(false);
    }
  }

  const triggerDownload = async () => {
    try {
      const link = await resolveDownloadUrl();
      triggerBrowserDownload(link);
      toast.success('Your Candor IDE download has started!');
    } catch (err) {
      toast.error('Failed to trigger download.');
    }
  };

  /** Stamp download intent before password login / SSO leave so post-auth resume works. */
  const markPendingDownload = (flowMode) => {
    try {
      sessionStorage.setItem(
        PENDING_DOWNLOAD_KEY,
        JSON.stringify({
          at: Date.now(),
          source: 'download_modal',
          mode: flowMode,
          flow: 'download',
          email: email ? email.trim().toLowerCase() : null,
          firstName: firstName || null,
          lastName: lastName || null,
        })
      );
    } catch {
      /* ignore */
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (!captchaVerified && process.env.NODE_ENV !== 'development') {
      toast.error('Please verify the reCAPTCHA first.');
      return;
    }
    if (!email || !password) return;

    setLoading(true);
    try {
      markPendingDownload('signin');
      await login(email.trim().toLowerCase(), password);
      toast.success('Successfully logged in.');
      onClose();
      triggerDownload();
    } catch (err) {
      toast.error(err.message || 'Sign in failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!captchaVerified && process.env.NODE_ENV !== 'development') {
      toast.error('Please verify the reCAPTCHA first.');
      return;
    }
    const trimmedEmail = email.trim().toLowerCase();
    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    const trimmedCompany = company.trim();
    if (!trimmedEmail || !password || !trimmedFirstName || !trimmedLastName) {
      toast.error('Email, password, first name, and last name are required.');
      return;
    }

    setLoading(true);
    try {
      markPendingDownload('register');
      const res = await fetch(apiUrl(getConfig('api.endpoints.raw.AUTH_REGISTER') || legacyApiPath('register')), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: trimmedEmail,
          password,
          first_name: trimmedFirstName,
          last_name: trimmedLastName,
          company: trimmedCompany,
          plan_name: 'Community Edition',
        }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Registration failed.');
      }

      toast.success('Verification code sent to your email.');
      setMode('verify');
    } catch (err) {
      toast.error(err.message || 'Registration failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const resOtp = await fetch(apiUrl(getConfig('api.endpoints.raw.AUTH_VERIFY_OTP') || legacyApiPath('verify_otp')), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          otp: otp.trim(),
        }),
      });

      if (!resOtp.ok) {
        throw new Error('Invalid verification code.');
      }

      await login(email.trim().toLowerCase(), password);
      toast.success('Email verified successfully.');
      onClose();
      triggerDownload();
    } catch (err) {
      toast.error(err.message || 'Verification failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      const res = await fetch(apiUrl(getConfig('api.endpoints.raw.AUTH_RESEND_OTP') || legacyApiPath('resend_otp')), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });

      if (!res.ok) throw new Error('Resend failed.');
      toast.success('Verification code resent.');
    } catch (err) {
      toast.error('Failed to resend code. Please try again.');
    }
  };

  // ── Password-reset step 1: send OTP ──────────────────────────────────────
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(apiUrl(getConfig('api.endpoints.raw.AUTH_FORGOT_PASSWORD') || legacyApiPath('forgot_password')), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Could not send reset code.');
      }
      toast.success(
        data.message ||
          'If an account exists for this email, a verification code has been sent.'
      );
      setResetOtp('');
      setNewPassword('');
      setConfirmPassword('');
      setForgotAwaitingCode(true);
    } catch (err) {
      toast.error(err.message || 'Failed to send reset code.');
    } finally {
      setLoading(false);
    }
  };

  // ── Password-reset step 2: OTP + new password ────────────────────────────
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }
    if (newPassword.length < 12 || newPassword.length > 34) {
      toast.error('Password must be 12–34 characters.');
      return;
    }
    if (!/[A-Z]/.test(newPassword)) { toast.error('Need at least one uppercase letter.'); return; }
    if (!/[a-z]/.test(newPassword)) { toast.error('Need at least one lowercase letter.'); return; }
    if (!/[0-9]/.test(newPassword)) { toast.error('Need at least one number.'); return; }
    if (!/[^A-Za-z0-9]/.test(newPassword)) { toast.error('Need at least one special character.'); return; }

    setLoading(true);
    try {
      const res = await fetch(apiUrl(getConfig('api.endpoints.raw.AUTH_RESET_PASSWORD') || legacyApiPath('reset_password')), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          otp: resetOtp.trim(),
          newPassword: newPassword,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Password reset failed.');
      }
      toast.success('Password reset! Please sign in to download.');
      setResetOtp('');
      setNewPassword('');
      setConfirmPassword('');
      switchMode('signin');
    } catch (err) {
      toast.error(err.message || 'Reset failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const headerTitle = {
    signin: 'Download Candor IDE',
    register: 'Download Candor IDE',
    verify: 'Verify Your Email',
    forgot: 'Reset Your Password',
    'reset-password': 'Set New Password',
  }[mode];

  const headerSubtitle = {
    signin: 'Already registered? Sign in with your work email.',
    register: 'New to Candor? Create a free account to continue.',
    verify: 'Enter the code we emailed you to start the download.',
    forgot: "Enter your email and we'll send a reset code.",
    'reset-password': 'Enter the reset code from your email and set a new password.',
  }[mode];

  const showTabs = mode === 'signin' || mode === 'register';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-3 sm:p-4 bg-slate-900/55 backdrop-blur-[3px]">
      <div className="relative bg-white rounded-2xl shadow-2xl shadow-slate-900/15 max-w-[26rem] w-full border border-slate-200/80 p-5 sm:p-6 animate-fade-in max-h-[min(92vh,44rem)] overflow-y-auto overscroll-contain">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 text-xl leading-none focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900/10"
          aria-label="Close modal"
        >
          &times;
        </button>

        <div className="text-center mb-4 pr-6">
          <img
            src={`${process.env.PUBLIC_URL || ''}/candor-brand-lockup.png`}
            alt="Candor"
            className="h-8 mx-auto mb-2 object-contain"
            style={{ maxWidth: '140px' }}
            onError={(e) => {
              e.target.src = `${process.env.PUBLIC_URL || ''}/candorlogo.png`;
              e.target.onerror = () => { e.target.src = defaultLogoCdnUrl(); };
            }}
          />
          <h2 className="text-lg font-bold text-slate-900 tracking-tight">{headerTitle}</h2>
          <p className="text-xs text-slate-500 mt-0.5 leading-snug">{headerSubtitle}</p>
        </div>

        {/* Sign In / Register tabs (only on main modes) */}
        {showTabs && (
          <div className="flex rounded-xl bg-slate-100/90 p-1 mb-4 text-sm" role="tablist">
            <button
              type="button"
              onClick={() => switchMode('signin')}
              className={`flex-1 py-2 text-center font-semibold rounded-lg transition-colors ${
                mode === 'signin'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={() => switchMode('register')}
              className={`flex-1 py-2 text-center font-semibold rounded-lg transition-colors ${
                mode === 'register'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              Register
            </button>
          </div>
        )}

        {/* ── Sign In form ── */}
        {mode === 'signin' && (
          <form onSubmit={handleSignIn} className="space-y-3">
            <CloudAuthButtons
              mode="login"
              density="compact"
              disabled={loading}
              layout="first"
              afterAuthIntent="download"
              onBeforeNavigate={() => markPendingDownload('signin')}
            />
            <div>
              <label htmlFor="signin-email" className="block text-xs font-semibold text-slate-600 mb-1.5">Work Email</label>
              <input
                id="signin-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@company.com"
                required
                className="w-full px-3.5 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50/50"
              />
            </div>
            <div>
              <label htmlFor="signin-pass" className="block text-xs font-semibold text-slate-600 mb-1.5">Password</label>
              <div className="relative flex items-center">
                <input
                  id="signin-pass"
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="w-full px-3.5 py-2 pr-10 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50/50"
                />
                <button
                  type="button"
                  onClick={() => setShowPass((v) => !v)}
                  className="absolute right-3 text-slate-400 hover:text-slate-700 focus:outline-none"
                  aria-label={showPass ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  <PasswordVisibilityIcon visible={showPass} />
                </button>
              </div>
            </div>
            <div className="flex justify-center py-1">
              <div id="download-recaptcha-signin"></div>
            </div>
            <button type="submit" disabled={loading} className="w-full py-2.5 px-4 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-md transition-all hover:-translate-y-0.5 disabled:opacity-50">
              {loading ? 'Signing In...' : 'Sign in to download'}
            </button>
            {/* Forgot password link */}
            <p className="text-center text-xs text-slate-500">
              <button
                id="download-forgot-password-btn"
                type="button"
                className="text-blue-600 font-semibold hover:underline"
                onClick={() => switchMode('forgot')}
              >
                Forgot password?
              </button>
            </p>
          </form>
        )}

        {/* ── Register form ── */}
        {mode === 'register' && (
          <form onSubmit={handleRegister} className="space-y-3">
            <CloudAuthButtons
              mode="register"
              density="compact"
              disabled={loading}
              layout="first"
              afterAuthIntent="download"
              onBeforeNavigate={() => markPendingDownload('register')}
            />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="reg-first-name" className="block text-xs font-semibold text-slate-600 mb-1.5">First Name</label>
                <input id="reg-first-name" type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Jane" required className="w-full px-3.5 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50/50" />
              </div>
              <div>
                <label htmlFor="reg-last-name" className="block text-xs font-semibold text-slate-600 mb-1.5">Last Name</label>
                <input id="reg-last-name" type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Smith" required className="w-full px-3.5 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50/50" />
              </div>
            </div>
            <div>
              <label htmlFor="reg-email" className="block text-xs font-semibold text-slate-600 mb-1.5">Work Email</label>
              <input id="reg-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" required className="w-full px-3.5 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50/50" />
            </div>
            <div>
              <label htmlFor="reg-company" className="block text-xs font-semibold text-slate-600 mb-1.5">Company (optional)</label>
              <input id="reg-company" type="text" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Your company" className="w-full px-3.5 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50/50" />
            </div>
            <div>
              <label htmlFor="reg-pass" className="block text-xs font-semibold text-slate-600 mb-1.5">Password</label>
              <input id="reg-pass" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Minimum 8 characters" minLength="8" required className="w-full px-3.5 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50/50" />
            </div>
            <div className="flex justify-center py-1">
              <div id="download-recaptcha-register"></div>
            </div>
            <button type="submit" disabled={loading} className="w-full py-2.5 px-4 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-md transition-all hover:-translate-y-0.5 disabled:opacity-50">
              {loading ? 'Creating Account...' : 'Create Account & Continue'}
            </button>
          </form>
        )}

        {/* ── Email OTP verify (after registration) ── */}
        {mode === 'verify' && (
          <form onSubmit={handleVerifyOtp} className="space-y-4">
            <p className="text-xs text-slate-600 text-center">
              Code sent to <strong>{email}</strong>
            </p>
            <div>
              <label htmlFor="verify-otp" className="block text-xs font-semibold text-slate-600 mb-1.5">Verification Code</label>
              <input
                id="verify-otp"
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="6-digit code"
                maxLength="8"
                required
                className="w-full px-3.5 py-2 border border-slate-200 rounded-lg text-sm text-center tracking-wider font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50/50"
              />
            </div>
            <button type="submit" disabled={loading} className="w-full py-2.5 px-4 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-md transition-all hover:-translate-y-0.5 disabled:opacity-50">
              {loading ? 'Verifying...' : 'Verify & Download'}
            </button>
            <div className="flex justify-between text-xs pt-1">
              <button type="button" onClick={handleResendOtp} className="text-blue-600 hover:underline">Resend code</button>
              <button type="button" onClick={() => switchMode('register')} className="text-slate-500 hover:underline">Back</button>
            </div>
          </form>
        )}

        {/* ── Forgot password: enter email ── */}
        {mode === 'forgot' && (
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div>
              <label htmlFor="forgot-email" className="block text-xs font-semibold text-slate-600 mb-1.5">Work Email</label>
              <input
                id="forgot-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@company.com"
                required
                className="w-full px-3.5 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50/50"
              />
            </div>
            {forgotAwaitingCode && (
              <p className="text-xs text-slate-600 text-center">
                If an account exists for this email, a code was sent. Enter it when you receive it.
              </p>
            )}
            <button type="submit" disabled={loading} className="w-full py-2.5 px-4 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-md transition-all hover:-translate-y-0.5 disabled:opacity-50">
              {loading ? 'Sending...' : (forgotAwaitingCode ? 'Resend Code →' : 'Send Reset Code →')}
            </button>
            {forgotAwaitingCode && (
              <button
                type="button"
                className="w-full py-2.5 px-4 rounded-xl text-sm font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 transition-all"
                onClick={() => switchMode('reset-password')}
              >
                I have a code →
              </button>
            )}
            <p className="text-center text-xs">
              <button type="button" className="text-slate-500 hover:underline" onClick={() => switchMode('signin')}>← Back to sign in</button>
            </p>
          </form>
        )}

        {/* ── Reset password: OTP + new password (combined, single API call) ── */}
        {mode === 'reset-password' && (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <p className="text-xs text-slate-600 text-center">
              Enter the code from your email for <strong>{email}</strong>
            </p>
            <div>
              <label htmlFor="dl-reset-otp" className="block text-xs font-semibold text-slate-600 mb-1.5">Reset Code</label>
              <input
                id="dl-reset-otp"
                type="text"
                value={resetOtp}
                onChange={(e) => setResetOtp(e.target.value)}
                placeholder="6-digit code from email"
                maxLength={8}
                required
                className="w-full px-3.5 py-2 border border-slate-200 rounded-lg text-sm text-center tracking-wider font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50/50"
              />
            </div>
            <div>
              <label htmlFor="dl-new-pwd" className="block text-xs font-semibold text-slate-600 mb-1.5">New Password</label>
              <div className="relative flex items-center">
                <input
                  id="dl-new-pwd"
                  type={showNewPwd ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 12 characters"
                  required
                  className="w-full px-3.5 py-2 pr-10 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50/50"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPwd((v) => !v)}
                  className="absolute right-3 text-slate-400 hover:text-slate-700 focus:outline-none"
                  aria-label={showNewPwd ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  <PasswordVisibilityIcon visible={showNewPwd} />
                </button>
              </div>
              {/* Live password strength checklist */}
              {newPassword && (
                <ul className="mt-2 space-y-1">
                  {[
                    { label: '12–34 characters', ok: newPassword.length >= 12 && newPassword.length <= 34 },
                    { label: 'Uppercase letter (A-Z)', ok: /[A-Z]/.test(newPassword) },
                    { label: 'Lowercase letter (a-z)', ok: /[a-z]/.test(newPassword) },
                    { label: 'Number (0-9)', ok: /[0-9]/.test(newPassword) },
                    { label: 'Special character (@#$%&! etc.)', ok: /[^A-Za-z0-9]/.test(newPassword) },
                  ].map(({ label, ok }) => (
                    <li key={label} className={`flex items-center gap-1.5 text-xs ${ok ? 'text-green-600' : 'text-slate-400'}`}>
                      <span aria-hidden="true">{ok ? '\u2713' : '\u25CB'}</span>{label}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <label htmlFor="dl-confirm-pwd" className="block text-xs font-semibold text-slate-600 mb-1.5">Confirm New Password</label>
              <div className="relative flex items-center">
                <input
                  id="dl-confirm-pwd"
                  type={showConfirmPwd ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter new password"
                  required
                  className="w-full px-3.5 py-2 pr-10 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-slate-50/50"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPwd((v) => !v)}
                  className="absolute right-3 text-slate-400 hover:text-slate-700 focus:outline-none"
                  aria-label={showConfirmPwd ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  <PasswordVisibilityIcon visible={showConfirmPwd} />
                </button>
              </div>
              {newPassword && confirmPassword && newPassword !== confirmPassword && (
                <p className="text-xs text-red-600 mt-1">Passwords do not match</p>
              )}
              {newPassword && confirmPassword && newPassword === confirmPassword && (
                <p className="text-xs text-green-600 mt-1">{'\u2713'} Passwords match</p>
              )}
            </div>
            <button type="submit" disabled={loading} className="w-full py-2.5 px-4 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-md transition-all hover:-translate-y-0.5 disabled:opacity-50">
              {loading ? 'Resetting...' : 'Reset Password →'}
            </button>
            <p className="text-center text-xs">
              <button type="button" className="text-slate-500 hover:underline" onClick={() => switchMode('signin')}>← Back to sign in</button>
            </p>
          </form>
        )}

        <div className="flex justify-center items-center gap-4 text-[10px] text-slate-400 mt-6 pt-4 border-t border-slate-100">
          <span>Secure download</span>
          <span>Verified account</span>
        </div>
      </div>
    </div>
  );
}
